import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import {
  RefreshCw, AlertTriangle, TrendingUp, Award, Phone, Map as MapIcon,
  MessageSquare, Clock, Activity, Star, TrendingDown, ArrowRight, Target
} from 'lucide-react';
import {
  ResponsiveContainer, AreaChart, Area, BarChart, Bar,
  XAxis, YAxis, Tooltip, CartesianGrid
} from 'recharts';
import api from '../utils/api';
import TikTokWordCloud from './TikTokWordCloud';
import './Dashboard.css';

const nf = (n) => (n == null ? '0' : Number(n).toLocaleString());

const timeAgo = (date) => {
  if (!date) return '';
  const d = new Date(date);
  const diff = Date.now() - d.getTime();
  if (isNaN(diff)) return '';
  const min = Math.floor(diff / 60000);
  if (min < 1) return 'just now';
  if (min < 60) return `${min}m ago`;
  const hr = Math.floor(min / 60);
  if (hr < 24) return `${hr}h ago`;
  const days = Math.floor(hr / 24);
  if (days < 7) return `${days}d ago`;
  return d.toLocaleDateString();
};

const clip = (txt, len = 140) => {
  if (!txt) return '';
  return txt.length > len ? `${txt.substring(0, len)}…` : txt;
};

const npsClass = (score) => {
  if (score >= 50) return 'positive';
  if (score >= 0) return 'neutral';
  return 'negative';
};

const sentimentClass = (score) => {
  if (score >= 20) return 'positive';
  if (score >= -20) return 'neutral';
  return 'negative';
};

const roasClass = (roas) => {
  if (roas == null) return 'neutral';
  if (roas >= 3) return 'positive';
  if (roas >= 1.5) return 'neutral';
  return 'negative';
};

const Dashboard = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);

  // Data buckets
  const [npsScores, setNpsScores] = useState([]);
  const [overallNps, setOverallNps] = useState(0);
  const [npsHistory, setNpsHistory] = useState([]);
  const [mapsSentiment, setMapsSentiment] = useState({ positive: 0, neutral: 0, negative: 0 });
  const [reviewStats, setReviewStats] = useState({ totalReviews: 0, averageRating: 0, ratingDistribution: {} });
  const [callsWeek, setCallsWeek] = useState({ total: 0, urgent: 0, urgentItems: [], calls: [] });
  const [urgentReviews, setUrgentReviews] = useState([]);
  const [lowStarReviews, setLowStarReviews] = useState([]);
  const [recentReviews, setRecentReviews] = useState([]);
  const [twOverview, setTwOverview] = useState(null);

  const loadAll = useCallback(async () => {
    const weekAgo = new Date(Date.now() - 7 * 24 * 60 * 60 * 1000).toISOString().split('T')[0];

    const settled = await Promise.allSettled([
      api.get('/api/nps/scores'),
      api.get('/api/dashboard/analytics'),
      api.get('/api/reviews/stats'),
      api.get(`/api/calls?startDate=${weekAgo}&limit=200&status=analyzed`),
      api.get('/api/ai/review-analysis?urgency_only=true&limit=30&sort_by=created_at&sort_order=desc'),
      api.get('/api/reviews?limit=10&sortBy=newest'),
      api.get('/api/nps/history'),
      api.get('/api/reviews?rating=1&limit=15&sortBy=newest'),
      api.get('/api/reviews?rating=2&limit=15&sortBy=newest'),
      api.get('/api/triplewhale/overview?period=30d')
    ]);

    // NPS scores
    if (settled[0].status === 'fulfilled' && settled[0].value.data?.success) {
      setNpsScores(settled[0].value.data.scores || []);
      setOverallNps(settled[0].value.data.overallNps || 0);
    }

    // Maps sentiment — API wraps payload under .analytics
    if (settled[1].status === 'fulfilled' && settled[1].value.data?.success) {
      const analytics = settled[1].value.data.analytics || {};
      const sData = analytics.sentimentData || [];
      const pct = {};
      for (const s of sData) pct[s.sentiment] = s.percentage || 0;
      setMapsSentiment({
        positive: pct.positive || 0,
        neutral: pct.neutral || 0,
        negative: pct.negative || 0
      });
    }

    // Review stats  — API wraps as { success, stats: { totalReviews, averageRating, ratingDistribution, ... } }
    if (settled[2].status === 'fulfilled' && settled[2].value.data?.success) {
      const stats = settled[2].value.data.stats || {};
      setReviewStats({
        totalReviews: stats.totalReviews || 0,
        averageRating: stats.averageRating || 0,
        ratingDistribution: stats.ratingDistribution || {}
      });
    }

    // Calls this week — urgency lives on nested analysis, not top-level
    if (settled[3].status === 'fulfilled' && settled[3].value.data?.success) {
      const calls = settled[3].value.data.calls || [];
      const total = settled[3].value.data.pagination?.total ?? calls.length;
      const urgentFromServer = settled[3].value.data.kpis?.urgentCount ?? null;
      const urgentList = calls.filter(c => c.analysis?.urgency_flag).slice(0, 8);
      setCallsWeek({
        total,
        urgent: urgentFromServer ?? urgentList.length,
        urgentItems: urgentList,
        calls
      });
    }

    // Urgent reviews (filter applied server-side via urgency_only=true)
    if (settled[4].status === 'fulfilled' && settled[4].value.data?.success) {
      setUrgentReviews((settled[4].value.data.analyses || []).slice(0, 15));
    }

    // Recent reviews (activity feed seed)
    if (settled[5].status === 'fulfilled' && settled[5].value.data?.success) {
      setRecentReviews(settled[5].value.data.reviews || []);
    }

    // NPS daily history
    if (settled[6].status === 'fulfilled' && settled[6].value.data?.success) {
      setNpsHistory(settled[6].value.data.history || []);
    }

    // Low-star reviews (1 + 2 stars) — what users mean when they say "urgent"
    const lowStar = [];
    if (settled[7].status === 'fulfilled' && settled[7].value.data?.success) {
      lowStar.push(...(settled[7].value.data.reviews || []));
    }
    if (settled[8].status === 'fulfilled' && settled[8].value.data?.success) {
      lowStar.push(...(settled[8].value.data.reviews || []));
    }
    setLowStarReviews(lowStar);

    // Triple Whale overview — silent on failure (e.g. account not yet synced)
    if (settled[9].status === 'fulfilled') {
      setTwOverview(settled[9].value.data || null);
    }
  }, []);

  useEffect(() => {
    let mounted = true;
    (async () => {
      try {
        await loadAll();
      } finally {
        if (mounted) setLoading(false);
      }
    })();
    return () => { mounted = false; };
  }, [loadAll]);

  const handleRefresh = async () => {
    setRefreshing(true);
    try { await loadAll(); }
    finally { setRefreshing(false); }
  };

  // Derived: Maps sentiment "net score" = positive% - negative%
  const mapsNet = useMemo(
    () => Math.round(mapsSentiment.positive - mapsSentiment.negative),
    [mapsSentiment]
  );

  // Urgent inbox: 1-2 star reviews + AI-flagged urgent reviews (guarded against
  // GPT false-positives on 4-5 star reviews) + urgent calls, deduped & date-sorted.
  const urgentInbox = useMemo(() => {
    const items = [];
    const seenReviewIds = new Set();

    // 1) Actual low-star reviews — these are the real complaints
    for (const review of lowStarReviews) {
      if (!review?.review_id || seenReviewIds.has(review.review_id)) continue;
      seenReviewIds.add(review.review_id);
      items.push({
        key: `r-${review.review_id}`,
        source: 'maps',
        title: review.reviewer_name || 'Anonymous',
        text: clip(review.review_text || '(no text)', 180),
        date: review.published_at_date,
        stars: review.stars,
        onClick: () => navigate('/google-maps', { state: { openReview: review } })
      });
    }

    // 2) AI-flagged urgent reviews — but skip 4-5 star (GPT false positives)
    for (const a of urgentReviews) {
      const review = a.reviews || {};
      if (!review.review_id || seenReviewIds.has(review.review_id)) continue;
      if ((review.stars || 0) >= 4) continue;
      seenReviewIds.add(review.review_id);
      const openReview = { ...review, __analysis: a };
      items.push({
        key: `r-${a.id || a.review_id}`,
        source: 'maps',
        title: review.reviewer_name || 'Anonymous',
        text: clip(review.review_text || a.urgency_reason, 180),
        date: review.published_at_date || a.created_at,
        stars: review.stars,
        onClick: () => navigate('/google-maps', { state: { openReview } })
      });
    }

    // 3) Urgent calls
    for (const c of callsWeek.urgentItems) {
      const a = c.analysis || {};
      items.push({
        key: `c-${c.id}`,
        source: 'call',
        title: c.caller_name || c.caller_number || 'Unknown caller',
        text: clip((a.summary_bullets && a.summary_bullets[0]) || a.issue_category || 'Urgent call flagged', 180),
        date: c.start_time,
        agent: c.agent_name,
        onClick: () => navigate(`/calls/${c.id}`)
      });
    }

    return items.sort((a, b) => new Date(b.date || 0) - new Date(a.date || 0)).slice(0, 12);
  }, [lowStarReviews, urgentReviews, callsWeek, navigate]);

  // NPS top 3 / bottom 3
  const { npsTop, npsBottom } = useMemo(() => {
    const qualified = npsScores.filter(s => (s.total_responses || 0) >= 5);
    const sorted = [...qualified].sort((a, b) => Number(b.nps_score) - Number(a.nps_score));
    return {
      npsTop: sorted.slice(0, 3),
      npsBottom: [...sorted].reverse().slice(0, 3)
    };
  }, [npsScores]);

  // NPS 30-day trend — sort oldest → newest so the line reads left-to-right
  const npsTrend = useMemo(() => {
    return [...npsHistory]
      .sort((a, b) => new Date(a.response_date) - new Date(b.response_date))
      .slice(-30)
      .map(d => ({
        date: d.response_date,
        label: new Date(d.response_date + 'T00:00:00').toLocaleDateString(undefined, { month: 'short', day: 'numeric' }),
        nps: Math.round(Number(d.nps_score) * 10) / 10,
        responses: d.total_responses
      }));
  }, [npsHistory]);

  // Sentiment donut data (three slices)
  const sentimentDonut = useMemo(() => ([
    { name: 'Positive', value: mapsSentiment.positive, color: '#10b981' },
    { name: 'Neutral',  value: mapsSentiment.neutral,  color: '#f59e0b' },
    { name: 'Negative', value: mapsSentiment.negative, color: '#ef4444' }
  ].filter(s => s.value > 0)), [mapsSentiment]);

  // Calls by day (last 7 days) — bucket calls from callsWeek.calls
  const callsByDay = useMemo(() => {
    const buckets = new Map();
    const today = new Date(); today.setHours(0, 0, 0, 0);
    for (let i = 6; i >= 0; i--) {
      const d = new Date(today); d.setDate(d.getDate() - i);
      const key = d.toISOString().split('T')[0];
      buckets.set(key, {
        date: key,
        label: d.toLocaleDateString(undefined, { weekday: 'short' }),
        total: 0,
        urgent: 0
      });
    }
    for (const c of callsWeek.calls) {
      if (!c.start_time) continue;
      const key = new Date(c.start_time).toISOString().split('T')[0];
      const b = buckets.get(key);
      if (!b) continue;
      b.total += 1;
      if (c.analysis?.urgency_flag) b.urgent += 1;
    }
    return [...buckets.values()];
  }, [callsWeek.calls]);

  // Activity feed: recent reviews (truncated text)
  const activityFeed = useMemo(() => {
    return recentReviews.slice(0, 10).map(r => ({
      key: `rec-${r.review_id}`,
      source: 'maps',
      title: r.reviewer_name || 'Anonymous',
      text: clip(r.review_text || r.review_text_translated, 120),
      openReview: r,
      date: r.published_at_date,
      stars: r.stars,
      branch: r.branches?.name,
      onClick: () => navigate('/google-maps', { state: { openReview: r } })
    }));
  }, [recentReviews, navigate]);

  const urgentCombinedCount = urgentReviews.length + callsWeek.urgent;

  if (loading) {
    return (
      <div className="dash-loading">
        <div className="dash-loading-spinner" />
        <p>Loading dashboard…</p>
      </div>
    );
  }

  return (
    <div className="dash">
      <div className="dash-header">
        <div>
          <h1 className="dash-title">Command Center</h1>
          <p className="dash-subtitle">Snapshot across Google Maps, NPS, and Avaya calls</p>
        </div>
        <button className="dash-refresh" onClick={handleRefresh} disabled={refreshing}>
          <RefreshCw size={14} className={refreshing ? 'dash-spin' : ''} />
          {refreshing ? 'Refreshing…' : 'Refresh'}
        </button>
      </div>

      {/* KPI Strip */}
      <div className="dash-kpis dash-kpis-five">
        <motion.div
          className="dash-kpi"
          initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.05 }}
        >
          <div className="dash-kpi-head">
            <span className="dash-kpi-icon nps"><Award size={16} /></span>
            Overall NPS
          </div>
          <div className={`dash-kpi-value ${npsClass(overallNps)}`}>{Number(overallNps).toFixed(1)}%</div>
          <div className="dash-kpi-sub">{nf(npsScores.length)} employees reporting</div>
        </motion.div>

        <motion.div
          className="dash-kpi"
          initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.1 }}
        >
          <div className="dash-kpi-head">
            <span className="dash-kpi-icon maps"><MapIcon size={16} /></span>
            Maps sentiment
          </div>
          <div className={`dash-kpi-value ${sentimentClass(mapsNet)}`}>
            {mapsNet > 0 ? '+' : ''}{mapsNet}
          </div>
          <div className="dash-kpi-sub">Positive − negative · {nf(reviewStats.totalReviews)} reviews</div>
          <div className="dash-kpi-chips">
            <span className="dash-chip green">{mapsSentiment.positive}% positive</span>
            <span className="dash-chip">{mapsSentiment.neutral}% neutral</span>
            <span className="dash-chip red">{mapsSentiment.negative}% negative</span>
          </div>
        </motion.div>

        <motion.div
          className="dash-kpi"
          initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.15 }}
        >
          <div className="dash-kpi-head">
            <span className="dash-kpi-icon calls"><Phone size={16} /></span>
            Calls (7 days)
          </div>
          <div className="dash-kpi-value">{nf(callsWeek.total)}</div>
          <div className="dash-kpi-sub">
            Avg rating {Number(reviewStats.averageRating || 0).toFixed(2)} · {callsWeek.urgent} urgent
          </div>
        </motion.div>

        <motion.div
          className="dash-kpi"
          initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.2 }}
        >
          <div className="dash-kpi-head">
            <span className="dash-kpi-icon urgent"><AlertTriangle size={16} /></span>
            Urgent now
          </div>
          <div className="dash-kpi-value negative">{nf(urgentCombinedCount)}</div>
          <div className="dash-kpi-chips">
            <span className="dash-chip blue">{urgentReviews.length} reviews</span>
            <span className="dash-chip purple">{callsWeek.urgent} calls</span>
          </div>
        </motion.div>

        <motion.div
          className="dash-kpi"
          initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.25 }}
          onClick={() => navigate('/triple-whale')}
          style={{ cursor: 'pointer' }}
          title="Open Triple Whale"
        >
          <div className="dash-kpi-head">
            <span className="dash-kpi-icon triplewhale"><Target size={16} /></span>
            Blended ROAS (30d)
          </div>
          <div className={`dash-kpi-value ${roasClass(twOverview?.kpis?.blendedRoas?.value)}`}>
            {twOverview?.kpis?.blendedRoas?.value != null
              ? `${Number(twOverview.kpis.blendedRoas.value).toFixed(2)}x`
              : '—'}
          </div>
          <div className="dash-kpi-sub">
            {twOverview?.kpis?.mer?.value != null
              ? `MER ${Number(twOverview.kpis.mer.value).toFixed(1)}%`
              : 'Awaiting first sync'}
          </div>
        </motion.div>
      </div>

      {/* Charts row */}
      <div className="dash-charts">
        {/* NPS trend */}
        <div className="dash-panel dash-chart-panel">
          <div className="dash-panel-head">
            <div className="dash-panel-title">
              <TrendingUp size={18} color="#34d399" /> NPS trend (last 30 days)
            </div>
            <div className="dash-panel-count">{npsTrend.length} data points</div>
          </div>
          {npsTrend.length === 0 ? (
            <div className="dash-empty">
              <Award size={36} />
              <div>No NPS history yet — run a sync from the NPS page.</div>
            </div>
          ) : (
            <div className="dash-chart-box">
            <ResponsiveContainer width="100%" height="100%">
              <AreaChart data={npsTrend} margin={{ top: 8, right: 12, left: 0, bottom: 0 }}>
                <defs>
                  <linearGradient id="npsFill" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="0%" stopColor="#10b981" stopOpacity={0.45} />
                    <stop offset="100%" stopColor="#10b981" stopOpacity={0} />
                  </linearGradient>
                </defs>
                <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.06)" />
                <XAxis
                  dataKey="label"
                  stroke="rgba(255,255,255,0.4)"
                  fontSize={11}
                  tickLine={false}
                />
                <YAxis
                  stroke="rgba(255,255,255,0.4)"
                  fontSize={11}
                  tickLine={false}
                  domain={[-100, 100]}
                  tickFormatter={(v) => `${v}%`}
                />
                <Tooltip
                  contentStyle={{
                    background: 'rgba(20,20,25,0.95)',
                    border: '1px solid rgba(255,255,255,0.12)',
                    borderRadius: 8,
                    color: '#fff',
                    fontSize: '0.8rem'
                  }}
                  formatter={(v, n) => [`${v}%`, 'NPS']}
                />
                <Area
                  type="monotone"
                  dataKey="nps"
                  stroke="#10b981"
                  strokeWidth={2}
                  fill="url(#npsFill)"
                  dot={false}
                />
              </AreaChart>
            </ResponsiveContainer>
            </div>
          )}
        </div>

        {/* Sentiment mix — CSS-only stacked bar + labeled stats */}
        <div className="dash-panel dash-chart-panel">
          <div className="dash-panel-head">
            <div className="dash-panel-title">
              <MapIcon size={18} color="#60a5fa" /> Maps sentiment mix
            </div>
            <div className="dash-panel-count">{mapsNet > 0 ? `+${mapsNet}` : mapsNet} net</div>
          </div>
          {sentimentDonut.length === 0 ? (
            <div className="dash-empty">
              <MessageSquare size={36} />
              <div>No sentiment data yet.</div>
            </div>
          ) : (
            <>
              <div className="dash-sent-stack">
                {sentimentDonut.map(s => (
                  <div
                    key={s.name}
                    className="dash-sent-seg"
                    style={{ flex: s.value, background: s.color }}
                    title={`${s.name} ${s.value}%`}
                  >
                    {s.value >= 8 && <span>{s.value}%</span>}
                  </div>
                ))}
              </div>
              <div className="dash-sent-rows">
                {sentimentDonut.map(s => (
                  <div key={s.name} className="dash-sent-row">
                    <span className="dash-sent-dot" style={{ background: s.color }} />
                    <span className="dash-sent-label">{s.name}</span>
                    <span className="dash-sent-value" style={{ color: s.color }}>
                      {s.value}%
                    </span>
                  </div>
                ))}
              </div>
            </>
          )}
        </div>

        {/* Calls by day */}
        <div className="dash-panel dash-chart-panel">
          <div className="dash-panel-head">
            <div className="dash-panel-title">
              <Phone size={18} color="#a78bfa" /> Calls by day (7d)
            </div>
            <div className="dash-panel-count">{callsWeek.total} total</div>
          </div>
          {callsByDay.every(d => d.total === 0) ? (
            <div className="dash-empty">
              <Phone size={36} />
              <div>No call activity this week.</div>
            </div>
          ) : (
            <div className="dash-chart-box">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={callsByDay} margin={{ top: 8, right: 12, left: 0, bottom: 0 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.06)" />
                <XAxis
                  dataKey="label"
                  stroke="rgba(255,255,255,0.4)"
                  fontSize={11}
                  tickLine={false}
                />
                <YAxis
                  stroke="rgba(255,255,255,0.4)"
                  fontSize={11}
                  tickLine={false}
                  allowDecimals={false}
                />
                <Tooltip
                  contentStyle={{
                    background: 'rgba(20,20,25,0.95)',
                    border: '1px solid rgba(255,255,255,0.12)',
                    borderRadius: 8,
                    color: '#fff',
                    fontSize: '0.8rem'
                  }}
                  cursor={{ fill: 'rgba(255,255,255,0.04)' }}
                />
                <Bar dataKey="total"  stackId="a" fill="#8b5cf6" radius={[0, 0, 4, 4]} name="Total" />
                <Bar dataKey="urgent" stackId="a" fill="#ef4444" radius={[4, 4, 0, 0]} name="Urgent" />
              </BarChart>
            </ResponsiveContainer>
            </div>
          )}
        </div>
      </div>

      {/* TikTok buzzwords (left) + NPS performers (right) — half/half */}
      <div className="dash-split-row">
        <TikTokWordCloud />

        <div className="dash-panel">
          <div className="dash-panel-head">
            <div className="dash-panel-title">
              <Award size={18} color="#34d399" /> NPS performers
            </div>
            <button
              className="dash-refresh"
              style={{ padding: '0.3rem 0.6rem', fontSize: '0.75rem' }}
              onClick={() => navigate('/nps')}
            >
              Full leaderboard <ArrowRight size={12} />
            </button>
          </div>

          {npsScores.length === 0 ? (
            <div className="dash-empty">
              <Award size={36} />
              <div>No NPS data yet. Run a sync from the NPS page.</div>
            </div>
          ) : (
            <div className="dash-nps-split">
              <div className="dash-mini-table">
                <div className="dash-mini-head top">
                  <TrendingUp size={12} /> Top 3
                </div>
                {npsTop.length === 0 ? (
                  <div className="dash-mini-row" style={{ color: 'rgba(255,255,255,0.4)' }}>
                    <span>Need ≥5 responses</span>
                  </div>
                ) : npsTop.map(s => (
                  <div key={s.sales_taker_code} className="dash-mini-row">
                    <span className="dash-mini-name">
                      {s.sales_taker_name || s.sales_taker_code}
                    </span>
                    <span className={`dash-mini-score ${npsClass(Number(s.nps_score))}`}>
                      {Number(s.nps_score).toFixed(1)}%
                    </span>
                  </div>
                ))}
              </div>

              <div className="dash-mini-table">
                <div className="dash-mini-head bottom">
                  <TrendingDown size={12} /> Bottom 3
                </div>
                {npsBottom.length === 0 ? (
                  <div className="dash-mini-row" style={{ color: 'rgba(255,255,255,0.4)' }}>
                    <span>Need ≥5 responses</span>
                  </div>
                ) : npsBottom.map(s => (
                  <div key={s.sales_taker_code} className="dash-mini-row">
                    <span className="dash-mini-name">
                      {s.sales_taker_name || s.sales_taker_code}
                    </span>
                    <span className={`dash-mini-score ${npsClass(Number(s.nps_score))}`}>
                      {Number(s.nps_score).toFixed(1)}%
                    </span>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Grid */}
      <div className="dash-grid">
        {/* Urgent Inbox */}
        <div className="dash-panel">
          <div className="dash-panel-head">
            <div className="dash-panel-title">
              <AlertTriangle size={18} color="#f87171" /> Urgent inbox
            </div>
            <div className="dash-panel-count">{urgentInbox.length} flagged</div>
          </div>

          {urgentInbox.length === 0 ? (
            <div className="dash-empty">
              <Award size={40} />
              <div>All clear. No urgent items across any channel.</div>
            </div>
          ) : (
            <div className="dash-feed">
              {urgentInbox.map(item => (
                <div
                  key={item.key}
                  className="dash-item urgent"
                  onClick={item.onClick}
                >
                  <div className={`dash-item-icon ${item.source === 'call' ? 'call' : 'maps'}`}>
                    {item.source === 'call' ? <Phone size={16} /> : <MapIcon size={16} />}
                  </div>
                  <div className="dash-item-body">
                    <div className="dash-item-title">
                      <span>{item.title}</span>
                      {item.stars != null && (
                        <span style={{ color: '#f59e0b', fontSize: '0.8rem' }}>
                          {'★'.repeat(item.stars)}
                        </span>
                      )}
                      <span className="dash-urgent-pill">
                        <AlertTriangle size={10} /> URGENT
                      </span>
                    </div>
                    {item.text && <div className="dash-item-text">{item.text}</div>}
                    <div className="dash-item-meta">
                      <span><Clock size={11} /> {timeAgo(item.date)}</span>
                      {item.agent && <span>Agent: {item.agent}</span>}
                      <span>{item.source === 'call' ? 'Avaya call' : 'Google review'}</span>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Right column: Activity feed */}
        <div className="dash-right-col">
          {/* Recent activity */}
          <div className="dash-panel">
            <div className="dash-panel-head">
              <div className="dash-panel-title">
                <Activity size={18} color="#60a5fa" /> Recent activity
              </div>
              <div className="dash-panel-count">Latest {activityFeed.length}</div>
            </div>

            {activityFeed.length === 0 ? (
              <div className="dash-empty">
                <MessageSquare size={36} />
                <div>No recent activity to show.</div>
              </div>
            ) : (
              <div className="dash-feed">
                {activityFeed.map(item => (
                  <div key={item.key} className="dash-item" onClick={item.onClick}>
                    <div className={`dash-item-icon ${item.source}`}>
                      {item.source === 'call' ? <Phone size={16} /> : <MapIcon size={16} />}
                    </div>
                    <div className="dash-item-body">
                      <div className="dash-item-title">
                        <span>{item.title}</span>
                        {item.stars != null && (
                          <span style={{ color: '#f59e0b', fontSize: '0.78rem' }}>
                            <Star size={11} style={{ verticalAlign: 'middle' }} /> {item.stars}
                          </span>
                        )}
                      </div>
                      {item.text && <div className="dash-item-text">{item.text}</div>}
                      <div className="dash-item-meta">
                        <span><Clock size={11} /> {timeAgo(item.date)}</span>
                        {item.branch && <span>{item.branch}</span>}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default Dashboard;
